import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Tabs, Tab, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import IPDMedicinePrescription from "./IPDMedicinePrescription";
import { exportIPDPrescription, printIPDPrescription } from "./IPDPrescriptionPrint";
import { FaFilePdf, FaPrint, FaSave, FaEdit, FaTrash, FaCheck, FaTimes, FaPlus, FaSearch, FaFileExport, FaEraser, FaWhatsapp, FaInfoCircle } from "react-icons/fa";
import ConfirmModal from "../../../UI/ConfirmModal";
import { toast } from "react-toastify";
import { getAuthToken } from "../../../../util/auth";
import { getApiChara } from "../../../../util/getApi";
import IPDPrescriptionSearchModal from "./IPDPrescriptionSearchModal";
import { useContext } from "react";
import AuthContext from "../../../../store/auth-context";

const PrescriptionModal = ({
    show,
    onClose,
    prescribedMedicines,
    setPrescribedMedicines,
    patientData,
    admissionData,
    isSavePrescription,
    setIsSavePrescription,
    isPrintPrescription,
    setIsPrintPrescription,
}) => {
    const { t } = useTranslation();
    const authCtx = useContext(AuthContext);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyList, setHistoryList] = useState([]);
    const isDischarged = admissionData?.status !== 'ACTIVE';
    const [editingId, setEditingId] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [diagnosis, setDiagnosis] = useState("");
    const [diagnosisList, setDiagnosisList] = useState([]);
    const [showSearchModal, setShowSearchModal] = useState(false);
    const [searchPrescriptions, setSearchPrescriptions] = useState({});

    // Delete Confirmation
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null); // ID or Array of items

    useEffect(() => {
        if (show) {
            const saveDefault = authCtx.hospitalData?.ipdSavePrescription || false;
            const printDefault = authCtx.hospitalData?.ipdPrintPrescription || false;
            setIsSavePrescription(saveDefault);
            setIsPrintPrescription(printDefault);
        }
    }, [show, setIsSavePrescription, setIsPrintPrescription, authCtx.hospitalData?.ipdSavePrescription, authCtx.hospitalData?.ipdPrintPrescription]);

    useEffect(() => {
        if (show && admissionData?.diagnosis && !diagnosis) {
            setDiagnosis(admissionData.diagnosis);
        }
    }, [show, admissionData, diagnosis]);

    // Fetch History when history modal is opened
    useEffect(() => {
        if (showHistoryModal && show && admissionData?.patientId?.patientId) {
            fetchHistory();
        }
    }, [showHistoryModal, show, admissionData]);

    const fetchHistory = async () => {
        try {
            const token = getAuthToken();
            const apiChar = getApiChara();
            // Use admissionId for the new grouped API
            const admissionId = admissionData.ipdAdmissionId;
            const response = await fetch(`${apiChar}/ipd/prescriptions/admission/${admissionId}/grouped`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                // If it's an array of groups, transform into object keyed by date
                if (Array.isArray(data)) {
                    const transformed = {};
                    data.forEach(group => {
                        if (group && group.length > 0) {
                            // Find the date (prefer the first item's dateTime)
                            const dateKey = group[0].dateTime || new Date().toISOString();
                            transformed[dateKey] = group;
                        }
                    });
                    setHistoryList(transformed);
                } else {
                    setHistoryList(data || {});
                }
            } else {
                console.error("Failed to fetch prescription history");
                setHistoryList({});
            }
        } catch (error) {
            console.error("Error fetching history:", error);
            setHistoryList({});
        }
    };

    const handleDeleteHistory = (id) => {
        setDeleteTarget(id);
        setShowDeleteConfirm(true);
    };

    const handleDeleteGroup = (items) => {
        setDeleteTarget(items);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;

        const token = getAuthToken();
        const apiChar = getApiChara();

        try {
            if (Array.isArray(deleteTarget)) {
                // Bulk Delete
                const deletePromises = deleteTarget.map(item =>
                    fetch(`${apiChar}/ipd/prescriptions/${item.id}`, {
                        method: "DELETE",
                        headers: { Authorization: `Bearer ${token}` }
                    })
                );

                const responses = await Promise.all(deletePromises);
                if (responses.every(res => res.ok)) {
                    toast.success("Prescription group deleted successfully");
                    fetchHistory();
                } else {
                    toast.error("Failed to delete some items");
                    fetchHistory();
                }
            } else {
                // Single Delete
                const response = await fetch(`${apiChar}/ipd/prescriptions/${deleteTarget}`, {
                    method: "DELETE",
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.ok) {
                    toast.success("Medicine deleted");
                    fetchHistory();
                } else {
                    toast.error("Failed to delete");
                }
            }
        } catch (error) {
            console.error(error);
            toast.error("Error deleting");
        } finally {
            setShowDeleteConfirm(false);
            setDeleteTarget(null);
        }
    };

    const handleSelectWholePrescription = (items) => {
        const formattedMeds = items.map(m => ({
            id: Date.now() + Math.random(),
            medicineName: m.medicineName || "",
            type: m.type || "",
            dosage: m.dosage || "",
            breakFastDose: m.breakFastDose || "",
            lunchDose: m.lunchDose || "",
            dinnerDose: m.dinnerDose || "",
            days: m.days || 0,
            instruction: m.instruction || ""
        }));
        setPrescribedMedicines(prev => [...prev, ...formattedMeds]);
        toast.success("Full prescription added to list!");
        setShowHistoryModal(false);
    };

    const handlePrintGroup = (items, date) => {
        // Use the print logic with the specific group items
        printIPDPrescription({ ...patientData, diagnosis: items[0]?.diagnosis || diagnosis }, items, authCtx);
    };

    const handleEditClick = (item) => {
        setEditingId(item.id);
        setEditFormData({ ...item });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditFormData({});
    };

    const handleUpdateHistory = async () => {
        try {
            const token = getAuthToken();
            const apiChar = getApiChara();

            const payload = {
                ...editFormData,
                dateTime: new Date().toISOString().replace("T", " ").substring(0, 19),
            };

            const response = await fetch(`${apiChar}/ipd/prescriptions/${editFormData.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                toast.success("Prescription updated");
                setEditingId(null);
                fetchHistory();
            } else {
                toast.error("Failed to update");
            }
        } catch (error) {
            toast.error("Error updating prescription");
        }
    };

    const handleEditChange = (e, field) => {
        setEditFormData(prev => ({ ...prev, [field]: e.target.value }));
    };

    const handleDiagnosisChange = (e) => {
        const value = e.target.value;
        setDiagnosis(value);
        fetchDiagnosisSuggestions(value);
    };

    const fetchDiagnosisSuggestions = async (term) => {
        if (!term || term.trim() === "") {
            setDiagnosisList([]);
            return;
        }

        const token = getAuthToken();
        if (!token || token === "EXPIRED") return;

        try {
            const apiChar = getApiChara();
            const apiUrl = `${apiChar}/diagnosis/${encodeURIComponent(term)}`;
            const response = await fetch(apiUrl, {
                headers: { Authorization: "Bearer " + token },
            });

            if (response.ok) {
                const data = await response.json();
                setDiagnosisList(data.map((d) => d.diagnosisName));
            }
        } catch (error) {
            console.error("Error fetching diagnosis suggestions:", error);
        }
    };

    const handleSearchPrescription = async () => {
        if (!diagnosis.trim()) {
            toast.warn("Please enter a diagnosis to search");
            return;
        }

        const token = getAuthToken();
        const apiChar = getApiChara();
        const apiUrl = `${apiChar}/ipd/saved-prescriptions/diagnosis/${encodeURIComponent(diagnosis.trim())}`;

        try {
            const response = await fetch(apiUrl, {
                headers: { Authorization: "Bearer " + token }
            });
            if (!response.ok) throw new Error("Fetch failed");
            const data = await response.json();
            if (Object.keys(data).length === 0) {
                toast.info("No saved prescriptions found for this diagnosis.");
            } else {
                setSearchPrescriptions(data);
                setShowSearchModal(true);
            }
        } catch (error) {
            console.error(error);
            toast.error("Error searching prescriptions");
        }
    };

    const handleSelectPrescription = (ipdMedicines) => {
        // Map raw OPD/Saved prescription data to our internal state format
        const formattedMeds = ipdMedicines.map(m => ({
            id: Date.now() + Math.random(),
            medicineName: m.medicineName || "",
            type: m.type || "",
            dosage: m.dosage || "",
            breakFastDose: m.breakFastDose || "",
            lunchDose: m.lunchDose || "",
            dinnerDose: m.dinnerDose || "",
            days: m.days || 0,
            instruction: m.notes || m.instruction || ""
        }));
        setPrescribedMedicines(prev => [...prev, ...formattedMeds]);
        toast.success("Prescription items added!");
    };

    const handleAddToPrescription = (item) => {
        const newMed = {
            id: Date.now(), // Temporary ID for frontend list
            medicineName: item.medicineName,
            type: item.type,
            dosage: item.dosage,
            breakFastDose: item.breakFastDose,
            lunchDose: item.lunchDose,
            dinnerDose: item.dinnerDose,
            days: item.days,
            instruction: item.instruction
        };
        setPrescribedMedicines(prev => [...prev, newMed]);
        toast.success("Added to New Prescription");
        setShowHistoryModal(false); // Close history modal
    };

    const handleExport = () => {
        if (prescribedMedicines.length === 0) {
            toast.warning("No medicines to export.");
            return;
        }
        exportIPDPrescription({ ...patientData, diagnosis }, prescribedMedicines, authCtx);
    };

    const handlePrint = () => {
        if (prescribedMedicines.length === 0) {
            toast.warning("No medicines to print.");
            return;
        }
        printIPDPrescription({ ...patientData, diagnosis }, prescribedMedicines, authCtx);
    };

    const handleWhatsAppShare = () => {
        if (prescribedMedicines.length === 0) {
            toast.warning("No medicines to share.");
            return;
        }

        const patientMobile = patientData?.mobile || "";
        const patientName = patientData?.name || "Patient";
        const cleanMobile = patientMobile.replace(/\D/g, '');

        let medicineText = "";
        if (prescribedMedicines && prescribedMedicines.length > 0) {
            medicineText = "\n\n*Prescribed Medicines:*";
            prescribedMedicines.forEach((med, index) => {
                const type = med.type ? `${med.type}. ` : "";
                const doses = `(${med.breakFastDose || '-'}/${med.lunchDose || '-'}/${med.dinnerDose || '-'})`;
                medicineText += `\n${index + 1}. ${type}${med.medicineName} - ${med.days} Days ${doses}`;
                if (med.instruction) medicineText += ` [${med.instruction}]`;
            });
        }

        const message = `Hello ${patientName}, your IPD prescription from ${authCtx.hospitalData?.hospitalName || 'the hospital'} is ready.${medicineText}\n\nRegards,\n${authCtx.hospitalData?.hospitalName || 'Hospital Management System'}`;

        const waUrl = `https://api.whatsapp.com/send?phone=91${cleanMobile}&text=${encodeURIComponent(message)}`;
        window.open(waUrl, "_blank");
    };

    const handleSave = async () => {
        // Validation: Must have medicines
        if (prescribedMedicines.length === 0) {
            toast.warning("Please add medicines before saving.");
            return;
        }

        if (!admissionData) {
            toast.error("Admission data is missing. Cannot save prescription.");
            return;
        }

        const token = getAuthToken();
        const apiChar = getApiChara();

        try {
            const savePromises = prescribedMedicines.map(med => {
                const payload = {
                    id: 0,
                    patientId: admissionData.patientId,
                    doctorId: admissionData.doctorId,
                    ipdAdmissionId: admissionData,
                    dateTime: new Date().toISOString().replace("T", " ").substring(0, 19),
                    medicineName: med.medicineName,
                    breakFastDose: med.breakFastDose,
                    lunchDose: med.lunchDose,
                    dinnerDose: med.dinnerDose,
                    notes: "",
                    instruction: med.instruction,
                    type: med.type,
                    days: Number(med.days) || 0,
                    dosage: med.dosage,
                    diagnosis: diagnosis,
                    savePrescription: isSavePrescription, // Dynamic
                    isSavePrescription: isSavePrescription // Dynamic
                };

                return fetch(`${apiChar}/ipd/prescriptions`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    },
                    body: JSON.stringify(payload)
                });
            });

            const responses = await Promise.all(savePromises);
            const allOk = responses.every(res => res.ok);

            if (allOk) {
                toast.success("Prescriptions saved successfully!");
                if (isPrintPrescription) {
                    handlePrint();
                }
                onClose();
            } else {
                toast.error("Some prescriptions failed to save.");
            }

        } catch (error) {
            console.error("Error saving prescriptions:", error);
            toast.error("An error occurred while saving prescriptions.");
        }
    };

    return (
        <>
            <style>
                {`
                .history-backdrop {
                    background-color: rgba(var(--primary-rgb), 0.5) !important;
                    backdrop-filter: blur(5px);
                }
                .dimmed-modal-content {
                    opacity: 0.2 !important;
                    filter: blur(3px) grayscale(50%);
                    transition: all 0.4s ease-in-out;
                    pointer-events: none;
                }
                .dimmed-modal-dialog {
                    transform: scale(0.97) !important;
                    transition: all 0.4s ease-in-out;
                }
                `}
            </style>
            <Modal
                show={show}
                onHide={onClose}
                size="xl"
                centered
                scrollable
                contentClassName={showHistoryModal || showSearchModal ? "dimmed-modal-content" : ""}
                dialogClassName={showHistoryModal || showSearchModal ? "dimmed-modal-dialog" : ""}
            >
                <Modal.Header closeButton className="modal-header-themed">
                    <Modal.Title className="w-100 d-flex justify-content-between align-items-center me-3">
                        <span>{t("IPD Medicine Prescription")}</span>
                        <Button
                            className="bg-light text-dark border py-1 px-3 fw-bold d-flex align-items-center gap-2"
                            size="sm"
                            onClick={() => setShowHistoryModal(true)}
                            style={{ opacity: 0.9 }}
                        >
                            <FaPlus /> {t("History")}
                        </Button>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ backgroundColor: "#f8f9fa", minHeight: '400px' }}>
                    {!isDischarged ? (
                        <div className="mb-4">
                            <Form.Group className="d-flex align-items-center gap-3">
                                <Form.Label className="fw-bold mb-0" style={{ minWidth: "80px" }}>{t("Diagnosis")}</Form.Label>
                                <div className="input-group" style={{ maxWidth: "400px" }}>
                                    <Form.Control
                                        type="text"
                                        placeholder={t("Enter Diagnosis")}
                                        value={diagnosis}
                                        onChange={handleDiagnosisChange}
                                        style={{ border: "1px solid #ced4da" }}
                                        list="ipd-diagnosis-list"
                                    />
                                    <datalist id="ipd-diagnosis-list">
                                        {diagnosisList.map((d, idx) => (
                                            <option key={idx} value={d} />
                                        ))}
                                    </datalist>
                                    <Button
                                        className="btn-premium"
                                        onClick={handleSearchPrescription}
                                        style={{ border: "none" }}
                                    >
                                        <FaSearch />
                                    </Button>
                                </div>
                            </Form.Group>
                        </div>
                    ) : (
                        <div className="alert alert-info py-2 px-3 mb-4 d-flex align-items-center gap-3 shadow-sm border-0 bg-info bg-opacity-10 text-info rounded-3">
                            <FaInfoCircle />
                            <div>
                                <h6 className="mb-0 fw-bold">{t("View Only Mode")}</h6>
                                <p className="mb-0 small">{t("Medicine entries are disabled for discharged patients. Please check History for past records.")}</p>
                            </div>
                        </div>
                    )}

                    {!isDischarged && (
                        <IPDMedicinePrescription
                            medicines={prescribedMedicines}
                            onMedicinesChange={setPrescribedMedicines}
                            diagnosis={diagnosis}
                            onDiagnosisChange={setDiagnosis}
                        />
                    )}

                    <hr />

                    <div className="d-flex flex-wrap gap-4 align-items-center justify-content-between p-3 bg-white rounded border">
                        <div className="d-flex gap-4 align-items-center">
                            <Form.Check
                                type="checkbox"
                                id="modal-save-prescription"
                                label={t("Save Prescription")}
                                checked={isSavePrescription}
                                onChange={(e) => setIsSavePrescription(e.target.checked)}
                                className="fw-bold"
                            />
                            <Form.Check
                                type="checkbox"
                                id="modal-print-prescription"
                                label={t("Print")}
                                checked={isPrintPrescription}
                                onChange={(e) => setIsPrintPrescription(e.target.checked)}
                                className="fw-bold"
                            />

                        </div>

                        <div className="d-flex gap-2 align-items-center flex-wrap">

                            <Button className="btn-export-premium" size="sm" onClick={handleExport} disabled={isDischarged}>
                                <FaFilePdf className="me-2" /> {t("Export PDF")}
                            </Button>

                            <Button className="btn-premium" size="sm" onClick={handlePrint} disabled={isDischarged}>
                                <FaPrint className="me-2" /> {t("Print")}
                            </Button>
                            <Button
                                className="btn-premium"
                                style={{ backgroundColor: isDischarged ? '#ccc' : '#25D366', borderColor: isDischarged ? '#ccc' : '#25D366' }}
                                size="sm"
                                onClick={handleWhatsAppShare}
                                disabled={isDischarged}
                            >
                                <FaWhatsapp className="me-2" /> {t("WhatsApp")}
                            </Button>
                            <Button
                                className="btn-premium fw-bold px-4"
                                onClick={handleSave}
                                size="sm"
                                disabled={isDischarged}
                            >
                                <FaSave className="me-2" /> {t("Save")}
                            </Button>
                            <Button className="btn-secondary-premium fw-bold px-3" size="sm" onClick={onClose}>
                                <FaTimes className="me-2" /> {t("Close")}
                            </Button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Prescription History Modal */}
            <Modal
                show={showHistoryModal}
                onHide={() => setShowHistoryModal(false)}
                size="xl"
                centered
                scrollable
                backdropClassName="history-backdrop"
            >
                <Modal.Header closeButton className="modal-header-themed shadow-sm">
                    <Modal.Title className="h5 fw-bold text-white mb-0">
                        <FaSearch className="me-2" style={{ fontSize: '0.9rem', opacity: 0.8 }} />
                        {t("PRESCRIPTION HISTORY")} - {patientData?.name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0 bg-light">
                    {!historyList || Object.keys(historyList).length === 0 ? (
                        <div className="text-center py-5 text-muted">No history found</div>
                    ) : (
                        <div className="accordion accordion-flush" id="historyAccordion">
                            {Object.entries(historyList).sort((a, b) => new Date(b[0]) - new Date(a[0])).map(([date, items], groupIndex) => (
                                <div className="accordion-item mb-3 border-0 shadow-sm rounded-4 overflow-hidden bg-white" key={date}>
                                    <h2 className="accordion-header" id={`heading${groupIndex}`}>
                                        <button
                                            className="accordion-button fw-bold"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target={`#collapse${groupIndex}`}
                                            aria-expanded="true"
                                            aria-controls={`collapse${groupIndex}`}
                                            style={{
                                                boxShadow: 'none',
                                                backgroundColor: 'rgba(var(--primary-rgb), 0.05)',
                                                color: 'var(--primary-color)',
                                                padding: '1.25rem'
                                            }}
                                        >
                                            <div className="d-flex flex-wrap w-100 justify-content-between align-items-center gap-2 me-3">
                                                <div className="d-flex align-items-center gap-2 flex-grow-1 text-start">
                                                    <span className="fs-5">📅</span>
                                                    <div className="d-flex flex-column">
                                                        <span className="small fw-bold">
                                                            {new Date(date).toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                                                        </span>
                                                        <span className="text-muted small" style={{ fontSize: '0.75rem' }}>
                                                            ({new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                                    <Button className="btn-outline-premium btn-sm py-1 px-2 fw-bold rounded-pill shadow-sm bg-white" style={{ fontSize: '0.75rem' }} onClick={(e) => { e.stopPropagation(); handleSelectWholePrescription(items); }}>
                                                        {t("Select")}
                                                    </Button>
                                                    <Button className="btn-outline-premium btn-sm py-1 px-2 rounded-pill shadow-sm bg-white" style={{ fontSize: '0.75rem' }} onClick={(e) => { e.stopPropagation(); handlePrintGroup(items, date); }}>
                                                        <FaPrint size={10} />
                                                    </Button>
                                                    <Button className="btn-outline-danger-premium btn-sm py-1 px-2 rounded-pill shadow-sm bg-white" style={{ fontSize: '0.75rem' }} onClick={(e) => { e.stopPropagation(); handleDeleteGroup(items); }}>
                                                        <FaTrash size={10} />
                                                    </Button>
                                                    <span className="badge bg-white text-teal border border-teal-subtle rounded-pill px-2 py-1 small" style={{ fontSize: '0.7rem' }}>
                                                        {items.length} {t("Meds")}
                                                    </span>
                                                </div>
                                            </div>
                                        </button>
                                    </h2>
                                    <div id={`collapse${groupIndex}`} className="accordion-collapse collapse show" aria-labelledby={`heading${groupIndex}`}>
                                        <div className="accordion-body p-0">
                                            <Table hover responsive className="mb-0 align-middle">
                                                <thead className="bg-light border-bottom">
                                                    <tr className="small text-uppercase text-secondary" style={{ backgroundColor: '#f8fafc' }}>
                                                        <th className="ps-4 py-3 border-0">{t("Medicine Name")}</th>
                                                        <th className="py-3 border-0">{t("Type")}</th>
                                                        <th className="py-3 border-0">{t("Dosage")}</th>
                                                        <th className="py-3 border-0">{t("BF")}</th>
                                                        <th className="py-3 border-0">{t("Lunch")}</th>
                                                        <th className="py-3 border-0">{t("Dinner")}</th>
                                                        <th className="py-3 border-0">{t("Days")}</th>
                                                        <th className="py-3 border-0">{t("Instruction")}</th>
                                                        {/* <th className="pe-4 text-center border-0">Actions</th> */}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {items.map((item) => (
                                                        <tr key={item.id} className="border-bottom">
                                                            {editingId === item.id ? (
                                                                <>
                                                                    <td className="ps-4 fw-medium">{item.medicineName}</td>
                                                                    <td>{item.type}</td>
                                                                    <td><Form.Control size="sm" value={editFormData.dosage} onChange={(e) => handleEditChange(e, 'dosage')} style={{ width: '80px' }} /></td>
                                                                    <td>
                                                                        <Form.Select size="sm" value={editFormData.breakFastDose || ""} onChange={(e) => handleEditChange(e, 'breakFastDose')} style={{ width: '85px' }}>
                                                                            <option value="">-</option>
                                                                            <option value="BEFORE">BEFORE</option>
                                                                            <option value="AFTER">AFTER</option>
                                                                        </Form.Select>
                                                                    </td>
                                                                    <td>
                                                                        <Form.Select size="sm" value={editFormData.lunchDose || ""} onChange={(e) => handleEditChange(e, 'lunchDose')} style={{ width: '85px' }}>
                                                                            <option value="">-</option>
                                                                            <option value="BEFORE">BEFORE</option>
                                                                            <option value="AFTER">AFTER</option>
                                                                        </Form.Select>
                                                                    </td>
                                                                    <td>
                                                                        <Form.Select size="sm" value={editFormData.dinnerDose || ""} onChange={(e) => handleEditChange(e, 'dinnerDose')} style={{ width: '85px' }}>
                                                                            <option value="">-</option>
                                                                            <option value="BEFORE">BEFORE</option>
                                                                            <option value="AFTER">AFTER</option>
                                                                        </Form.Select>
                                                                    </td>
                                                                    <td><Form.Control size="sm" value={editFormData.days} onChange={(e) => handleEditChange(e, 'days')} style={{ width: '60px' }} /></td>
                                                                    <td><Form.Control size="sm" value={editFormData.instruction} onChange={(e) => handleEditChange(e, 'instruction')} /></td>
                                                                    {/* <td>{item.doctorId?.firstName}</td> */}
                                                                    <td className="pe-4 text-center">
                                                                        <div className="d-flex gap-2 justify-content-center">
                                                                            <Button className="btn-premium rounded-circle p-0 d-flex align-items-center justify-content-center" size="sm" onClick={handleUpdateHistory} style={{ width: '28px', height: '28px' }}><FaCheck size={12} /></Button>
                                                                            <Button className="btn-secondary-premium rounded-circle p-0 d-flex align-items-center justify-content-center" size="sm" onClick={handleCancelEdit} style={{ width: '28px', height: '28px' }}><FaTimes size={12} /></Button>
                                                                        </div>
                                                                    </td>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <td className="ps-4 fw-medium text-dark">{item.medicineName}</td>
                                                                    <td className="text-secondary small">{item.type}</td>
                                                                    <td className="small">{item.dosage}</td>
                                                                    <td className="small text-muted">{item.breakFastDose || "-"}</td>
                                                                    <td className="small text-muted">{item.lunchDose || "-"}</td>
                                                                    <td className="small text-muted">{item.dinnerDose || "-"}</td>
                                                                    <td className="fw-bold text-teal small">{item.days}</td>
                                                                    <td className="small text-muted fst-italic">{item.instruction}</td>
                                                                    {/* <td className="small text-muted">{item.doctorId?.firstName}</td> */}
                                                                    {/* <td className="pe-4 text-center">
                                                                        <div className="d-flex gap-2 justify-content-center">
                                                                            <Button variant="light" size="sm" onClick={() => handleAddToPrescription(item)} title="Add to New" className="text-primary border-0 rounded-circle p-0 d-flex align-items-center justify-content-center" style={{ width: '30px', height: '30px', backgroundColor: '#e6f7ff' }}><FaPlus /></Button>
                                                                            <Button variant="light" size="sm" onClick={() => handleEditClick(item)} title="Edit" className="text-secondary border-0 rounded-circle p-0 d-flex align-items-center justify-content-center" style={{ width: '30px', height: '30px' }}><FaEdit /></Button>
                                                                            <Button variant="light" size="sm" onClick={() => handleDeleteHistory(item.id)} title="Delete" className="text-danger border-0 rounded-circle p-0 d-flex align-items-center justify-content-center" style={{ width: '30px', height: '30px' }}><FaTrash /></Button>
                                                                        </div>
                                                                    </td> */}
                                                                </>
                                                            )}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="bg-white border-top">
                    <Button
                        className="btn-secondary-premium px-5 shadow-sm"
                        onClick={() => setShowHistoryModal(false)}
                    >
                        <FaTimes className="me-2" /> {t("Close")}
                    </Button>
                </Modal.Footer>
            </Modal>

            {showSearchModal && (
                <IPDPrescriptionSearchModal
                    show={showSearchModal}
                    onClose={() => setShowSearchModal(false)}
                    prescriptions={searchPrescriptions}
                    onSelect={handleSelectPrescription}
                />
            )}

            <ConfirmModal
                show={showDeleteConfirm}
                onHide={() => setShowDeleteConfirm(false)}
                onConfirm={confirmDelete}
                title={t("Delete Prescription")}
                message={
                    Array.isArray(deleteTarget)
                        ? t("Are you sure you want to delete all {{count}} medicines in this prescription?", { count: deleteTarget.length })
                        : t("Are you sure you want to delete this medicine?")
                }
                variant="danger"
            />
        </>
    );
};

export default PrescriptionModal;
